import type { Message, Project, Session } from "./types";

const HUB_BASE_URL = "https://code.opzero.sh";

/**
 * What sort of machine this is, as stored by the hub rather than derived here.
 * `self_hosted` is a box the user runs; `cloud` is a runner hosted for them.
 */
export type MachineKind = "self_hosted" | "cloud";

/**
 * Category labels and their display order, shared by every picker surface so
 * the machines view and the session launcher cannot name the same category two
 * different ways. Self-hosted leads: it is what every existing user has.
 */
export const MACHINE_KIND_ORDER: readonly MachineKind[] = ["self_hosted", "cloud"];

export const MACHINE_KIND_LABEL: Record<MachineKind, string> = {
  self_hosted: "Self-hosted",
  cloud: "Cloud",
};

export interface HubMachine {
  machineId: string;
  hostname: string;
  os: string;
  arch: string;
  cpus: number;
  memoryGB: number;
  codezVersion: string;
  kind: MachineKind;
  /** Who hosts a cloud machine. Null for self-hosted. */
  provider: string | null;
  online: boolean;
  /**
   * Whether the hub holds a wake URL for this machine. Replaces asking by
   * attempting a wake and substring-matching the error message.
   */
  wakeable: boolean;
  lastSeenAt: number;
  createdAt: number;
  repos: Array<{ path: string; remote?: string }>;
  activeSessionCount: number;
}

/**
 * The machine payload as it may actually arrive. `kind`, `provider`, and
 * `wakeable` are optional here on purpose: this SPA and the Hub Worker are
 * updated independently — a user upgrades their local CodeZero whenever they
 * choose — so a client that knows about these fields will sometimes talk to a
 * hub that does not send them yet.
 */
type RawHubMachine = Omit<HubMachine, "kind" | "provider" | "wakeable"> &
  Partial<Pick<HubMachine, "kind" | "provider" | "wakeable">>;

/**
 * Normalizes one machine so every consumer downstream can treat the category
 * axis as total. Getting this wrong is not a cosmetic problem: a machine whose
 * `kind` matches no category is filtered out of every group, which would empty
 * the machines view and the session launcher against an older hub.
 *
 * `wakeable` defaults to true when absent, which restores the pre-field
 * behavior of offering the button and letting the request answer — strictly
 * better than hiding wake from a machine that can be woken.
 */
function normalizeMachine(machine: RawHubMachine): HubMachine {
  return {
    ...machine,
    kind: machine.kind === "cloud" ? "cloud" : "self_hosted",
    provider: machine.provider ?? null,
    wakeable: machine.wakeable ?? true,
  };
}

async function req<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${HUB_BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    let message = `${res.status} ${res.statusText}`;
    try {
      const parsed = JSON.parse(body) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch {
      if (body) message = body;
    }
    const error = new Error(message) as Error & { status?: number };
    error.status = res.status;
    throw error;
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

export interface WakeResult {
  status: "online" | "waking";
}

export const hubApi = {
  listMachines: async (token: string): Promise<{ machines: HubMachine[] }> => {
    const { machines } = await req<{ machines: RawHubMachine[] }>(token, "/api/machines");
    return { machines: machines.map(normalizeMachine) };
  },
  wakeMachine: (token: string, machineId: string) =>
    req<WakeResult>(token, `/api/machines/${encodeURIComponent(machineId)}/wake`, { method: "POST" }),
  listProjects: (token: string, machineId: string) =>
    req<{ projects: Project[] }>(token, `/api/machines/${encodeURIComponent(machineId)}/projects`),
  listSessions: (token: string, machineId: string, slug: string) =>
    req<{ sessions: Session[] }>(
      token,
      `/api/machines/${encodeURIComponent(machineId)}/projects/${encodeURIComponent(slug)}/sessions`,
    ),
  createSession: (
    token: string,
    machineId: string,
    slug: string,
    cwd?: string,
    permissionMode?: string,
    model?: string,
  ) =>
    req<{ sessionId: string; cwd: string }>(
      token,
      `/api/machines/${encodeURIComponent(machineId)}/projects/${encodeURIComponent(slug)}/sessions`,
      { method: "POST", body: JSON.stringify({ cwd, permissionMode, model }) },
    ),
  getSession: (token: string, machineId: string, slug: string, sessionId: string) =>
    req<{ session: Session; messages: Message[] }>(
      token,
      `/api/machines/${encodeURIComponent(machineId)}/sessions/${encodeURIComponent(sessionId)}?slug=${encodeURIComponent(slug)}`,
    ),
  prompt: (token: string, machineId: string, sessionId: string, text: string, slug: string) =>
    req<{ ok: true }>(token, `/api/machines/${encodeURIComponent(machineId)}/sessions/${encodeURIComponent(sessionId)}/prompt`, {
      method: "POST",
      body: JSON.stringify({ text, slug }),
    }),
  abort: (token: string, machineId: string, sessionId: string) =>
    req<void>(token, `/api/machines/${encodeURIComponent(machineId)}/sessions/${encodeURIComponent(sessionId)}/abort`, {
      method: "POST",
    }),
};
